<template>
  <div>
    <draggable
      :list="concerns"
      :item-key="(item, index) => index"
      @start="isDragging = true"
      @end="isDragging = false"
      @update="onUpdate"
      :disabled="!dragEnabled"
      :class="'section-'+sectionOGIndex+' grid grid-cols-1 gap-3 mb-5 md:gap-5 md:grid-cols-2 lg:grid-cols-4'"
    >
      <template #item="{ element, index }">
        <button
          type="button"
          @click="concernBtnClick(index, popupMode, element)"
          class="flex rounded-[8px] border border-brand-indigo group bg-white items-center gap-4 w-full p-3 hover:bg-brand-light-purple"
          :disabled="sectionOGIndex !== showSectionDetailIndex"
        >
          <svg
            class='icon size-3 icon-drag'
            :class="{ 'hidden': !dragEnabled, 'block': dragEnabled }"
            aria-hidden='true'
          >
            <use xlink:href='#icon-drag'></use>
          </svg>
          <span class="font-semibold paragraph-small text-brand-eggplant">
            {{ element.replace(/:\[text_field\]:/g,'') }}
          </span>
          <svg
            class='ml-auto icon size-5 icon-edit text-brand-gray'
            :class="{ 'hidden': dragEnabled || sectionOGIndex !== showSectionDetailIndex, 'block': !dragEnabled }"
            aria-hidden='true'
          >
            <use xlink:href='#icon-edit'></use>
          </svg>
        </button>
      </template>
    </draggable>

    <div class="grid grid-cols-1 gap-3 mb-5 md:gap-5 md:grid-cols-2 lg:grid-cols-4"
      v-show="sectionOGIndex !== showSectionDetailIndex">
      <button
        type="button"
        class="w-full create-concern-trigger flex rounded-[8px] border border-brand-indigo group hover:bg-brand-indigo items-center justify-between p-3 bg-brand-light-purple"
        @click="createConcernTrigger(sectionOGIndex, 'concern')"
        :data-og="sectionOGIndex">
        <span class="font-semibold paragraph-small text-brand-indigo group-hover:text-white">
          Add Concern
        </span>
        <div class="icon-wrapper group-hover:bg-white rounded-full size-[18px] bg-brand-indigo flex items-center justify-center">
          <svg class='icon size-[10px] group-hover:text-brand-indigo icon-plus text-white' aria-hidden='true'><use xlink:href='#icon-plus'></use></svg>
        </div>
      </button>

      <button
        type="button"
        class="w-full create-field-trigger flex rounded-[8px] border border-brand-indigo group hover:bg-brand-indigo items-center justify-between p-3 bg-brand-light-purple"
        @click="createConcernTrigger(sectionOGIndex, 'text_field')"
        :data-og="sectionOGIndex">
        <span class="font-semibold paragraph-small text-brand-indigo group-hover:text-white">
          Add Text Field to Form
        </span>
        <div class="icon-wrapper group-hover:bg-white rounded-full size-[18px] bg-brand-indigo flex items-center justify-center">
          <svg class='icon size-[10px] group-hover:text-brand-indigo icon-plus text-white' aria-hidden='true'><use xlink:href='#icon-plus'></use></svg>
        </div>
      </button>
    </div>
  </div>
    <!-- concern popup -->
  <div v-if="showConcernPopup" @click="closePopup" class="fixed -translate-x-1/2 -translate-y-1/2 z-[90] size-full bg-brand-scrim top-1/2 left-1/2"></div>
  <div v-if="showConcernPopup" class="w-[575px] max-w-[90vw] z-[91] rounded-[8px] p-6 md:p-12 mx-auto bg-white fixed top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2">
    <button class="mfp-close size-10" @click="closePopup">
      <svg class="rotate-45 pointer-events-none size-6 icon icon-plus text-brand-medium-gray" aria-hidden="true"><use xlink:href="#icon-plus"></use></svg>
    </button>
    <p class="mb-3 text-center hdg text-brand-wicked">
      {{ popupTitle }}
    </p>
    <p class="mb-4 text-center text-brand-medium-gray paragraph-default">
      {{ popupDescription }}
    </p>
    <div class="form">
      <input type="hidden" name="section-key" id="section-key" value="">
      <input type="hidden" name="section-name" id="section-name" value="">

      <label for="create-form-concern-name">{{ popupFieldLabel }}</label>
      <input v-model="cleanConcernName" type="text" name="create-form-concern-name" id="create-form-concern-name" value="">
      <p class="field_description">{{ popupFieldDescription }}</p>

      <input
        @click.prevent="addConcern"
        id="submit-new-concern"
        :class="{ 'border border-form-error' : errorMessage, '' : !errorMessage }"
        class="w-full mt-4 form-submit btn-primary" type="submit" name="add_section" value="Continue"
        v-if="popupMode == 'addConcern' || popupMode == 'addTextField'"
      />


      <div class="flex flex-col items-center gap-2 md:gap-3 md:flex-row" v-else>
        <input
          @click="saveConcern"
          id="submit-new-concern"
          class="w-full mt-0 form-submit btn-primary" type="submit" name="add_section" value="Save Updates">

          <input
          @click="deleteConcern"
          id="delete-concern"
          class="w-full mt-0 md:w-fit form-submit btn-secondary" type="submit" name="delete_section" value="Delete Concern">
      </div>

      <div v-if="errorMessage" class="mt-2 text-form-error">{{ errorMessage }}</div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick, computed } from 'vue';
import draggable from "vuedraggable";

const props = defineProps({
  concerns: Array,
  dragEnabled: Boolean,
  ogindex: Number,
  showSectionDetailIndex: Number,
});

const emit = defineEmits(['updateConcern', 'createConcernTrigger']);
const popupTitle = ref('Add Concern');
const popupDescription = ref('Add your a concern to your section. It is recommended these concerns are only 1-2 words. You can enter multiple concerns at once using a comma seprated list.');
const popupFieldLabel = ref('Concern');
const popupFieldDescription = ref('e.g. “Wrinkles”');
const showPopup = ref(false);
const showConcernPopup = ref(false);
const selectedConcernIndex = ref(null);
const concernName = ref('');
const sectionOGIndex = ref(props.ogindex);
const errorMessage = ref('');
const popupMode = ref('addConcern');

const onUpdate = (event) => {
  emit('updateConcern', props.concerns);
};

const cleanConcernName = computed({
  get: () => concernName.value.replace(/:\[text_field\]:/g, ''),
  set: (val) => {
    concernName.value = val // or transform it if needed before storing
  }
});

const createConcernTrigger = (index, operation, element) => {
  showConcernPopup.value = true;
  if(operation == 'concern') {
    popupMode.value ='addConcern';
  }else{
    popupMode.value ='addTextField';
    popupTitle.value = 'Add Text Field';
    popupDescription.value = 'Add your a text field to your section for the user to fill out';
    popupFieldLabel.value = 'Field Name';
    popupFieldDescription.value = 'e.g. "Other"';
  }
}

const addConcern = () => {
  const inputConcerns = concernName.value
    .split(',')
    .map(c => c.trim())
    .filter(c => c.length >= 3 && !props.concerns.includes(c))
    .map(c => popupMode.value == 'addConcern' ? c : `${c}:[text_field]:`);

  if (inputConcerns.length > 0) {
    props.concerns.push(...inputConcerns);
    concernName.value = '';
    errorMessage.value = '';
    closePopup();
  } else {
    errorMessage.value = 'Each concern must be unique and at least 3 characters long.';
  }
};

const concernBtnClick = async (index, _popupMode, element) => {
  if(!props.dragEnabled) {
    // edit button popup
    selectedConcernIndex.value = index;
    concernName.value = props.concerns[selectedConcernIndex.value];

    if(element !== undefined && !element.includes('text_field')) {
      // alert('editing concern');
      popupMode.value = 'editConcern';
      popupTitle.value = 'Edit Concern';
      popupDescription.value = 'Edit your concern in your section. It is recommended these concerns are only 1-2 words. You can enter multiple concerns at once using a comma seprated list.';
      popupFieldLabel.value = 'Field Name';
      popupFieldDescription.value = 'e.g. "Wrinkles"';
    } else if(element.includes('text_field')) {
      // alert('editing text field');
      popupMode.value = 'editTextField';
      popupTitle.value = 'Edit Text Field';
      popupDescription.value = 'Edit your text field in your section for the user to fill out.';
      popupFieldLabel.value = 'Field Name';
      popupFieldDescription.value = 'e.g. "Other"';
    } else {
      // alert('adding concern');
      popupMode.value = 'addConcern';
      popupTitle.value = 'Add Concern';
      popupDescription.value = 'Add your concern to your section. It is recommended these concerns are only 1-2 words. You can enter multiple concerns at once using a comma seprated list.';
      popupFieldLabel.value = 'Field Name';
      popupFieldDescription.value = 'e.g. "Wrinkles"';
    }

    await nextTick();
    showConcernPopup.value = true;
  }
}

const closePopup = () => {
  showPopup.value = false;
  showConcernPopup.value = false;
  popupMode.value ='addConcern';
  popupTitle.value = 'Add Concern';
  popupDescription.value = 'Add a concern to your section. It is recommended these concerns are only 1-2 words. You can enter multiple concerns at once using a comma seprated list.';
  popupFieldLabel.value = 'Field Name';
  popupFieldDescription.value = 'e.g. "Wrinkles"';
  concernName.value = '';
}

const saveConcern = () => {
  if(popupMode.value == 'editTextField') {
    concernName.value = concernName.value + ':[text_field]:';
  }
  props.concerns[selectedConcernIndex.value] = concernName.value;
  closePopup();
}

const deleteConcern = () => {
  props.concerns.splice(selectedConcernIndex.value, 1);
  closePopup();
}

onMounted(() => {
  moveTriggersToEnd();
});

const moveTriggersToEnd = () => {
  const triggers = document.querySelectorAll(".create-concern-trigger");
  const fieldTriggers = document.querySelectorAll(".create-field-trigger");

  triggers.forEach((trigger) => {
    const parentContainerIndex = $(trigger).attr("data-og");

    if (parentContainerIndex) {
      $('.section-'+parentContainerIndex).append(trigger);
    }
  });

  fieldTriggers.forEach((trigger) => {
    const parentContainerIndex = $(trigger).attr("data-og");

    if (parentContainerIndex) {
      $('.section-'+parentContainerIndex).append(trigger);
    }
  });
};

watch(
  () => props.concerns,
  () => {
    console.log('Array changed, waiting for DOM update...');
    nextTick(() => {
      moveTriggersToEnd();
    });
  },
  { deep: true }
);
</script>
